import React, { useState } from 'react';
import { MdDashboard, MdPerson } from 'react-icons/md';
import AuthService from '../services/authService';
import axiosInstance from '../utils/axiosInstance';
import { getImageURL } from '../utils/api';
import ImpersonationBanner from './ImpersonationBanner';

const NAV = [
  { name: 'Dashboard', href: '/lab/dashboard', icon: <MdDashboard /> },
  { name: 'Profile', href: '/lab/profile', icon: <MdPerson /> },
];

function LabSidebar({ isOpen }) {
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  return (
    <aside
      className="fixed inset-y-0 left-0 z-50 flex flex-col border-r border-gray-200 bg-white shadow-sm transition-all duration-300"
      style={{ width: isOpen ? 256 : 64 }}
    >
      <div className="flex h-16 items-center justify-center border-b border-gray-200 px-4">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-600 text-xs font-bold text-white">LB</div>
          {isOpen && <span className="text-base font-bold text-gray-900">Lab Assistant</span>}
        </div>
      </div>
      <nav className="flex-1 space-y-1.5 overflow-y-auto px-3 py-4">
        {NAV.map((item) => {
          const active = currentPath.startsWith(item.href);
          return (
            <a
              key={item.name}
              href={item.href}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm no-underline ${active ? 'bg-teal-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
            >
              <span className="text-lg">{item.icon}</span>
              {isOpen && <span>{item.name}</span>}
            </a>
          );
        })}
      </nav>
      {isOpen && (
        <div className="border-t border-gray-200 p-4 text-xs text-gray-500">
          <div className="font-semibold">Lab Assistant Panel</div>
        </div>
      )}
    </aside>
  );
}

function LabTopBar({ title, onMenuClick }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [broken, setBroken] = useState(false);
  const staff = AuthService.getUser()?.superAdmin?.staff || {};
  const name = staff.name || 'Lab Assistant';

  const handleLogout = async () => {
    try { await axiosInstance.post('/logout'); } catch {}
    AuthService.clear();
    window.location.href = '/';
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-gray-200 bg-white px-6 shadow-sm">
      <div className="flex items-center gap-4">
        <button type="button" onClick={onMenuClick} aria-label="Toggle menu" className="rounded-lg p-2 text-gray-600 hover:bg-gray-100">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" /></svg>
        </button>
        {title && <h1 className="text-lg font-semibold text-gray-900">{title}</h1>}
      </div>
      <div className="relative">
        <button type="button" onClick={() => setMenuOpen((v) => !v)} className="flex items-center gap-2 rounded-lg px-3 py-1.5 hover:bg-gray-100">
          {staff.imagePath && !broken ? (
            <img src={getImageURL(staff.imagePath)} alt={name} onError={() => setBroken(true)} className="h-8 w-8 rounded-full object-cover" />
          ) : (
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-teal-600 text-xs font-semibold text-white">
              {name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
            </span>
          )}
          <span className="text-sm font-medium text-gray-700">{name}</span>
        </button>
        {menuOpen && (
          <div className="absolute right-0 mt-2 w-56 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
            <div className="border-b border-gray-200 px-4 py-2.5">
              <p className="text-sm font-semibold text-gray-900">{name}</p>
              <p className="mt-0.5 text-xs text-gray-500">{staff.officeEmail || staff.gmail || ''}</p>
              <span className="mt-2 inline-block rounded-md border border-teal-200 bg-teal-50 px-2.5 py-1 text-xs font-semibold text-teal-800">Lab Assistant</span>
            </div>
            <button type="button" onClick={() => { window.location.href = '/lab/profile'; }} className="block w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100">
              Your Profile
            </button>
            <button type="button" onClick={handleLogout} className="block w-full border-t border-gray-200 px-4 py-2.5 text-left text-sm text-red-600 hover:bg-gray-100">
              Sign Out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

export default function LabLayout({ children, title }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <LabSidebar isOpen={sidebarOpen} />
      <div className="flex flex-1 flex-col overflow-hidden transition-all duration-300" style={{ marginLeft: sidebarOpen ? 256 : 80 }}>
        <ImpersonationBanner theme="emerald" />
        <LabTopBar title={title} onMenuClick={() => setSidebarOpen((v) => !v)} />
        <main className="flex-1 overflow-y-auto overflow-x-hidden bg-gray-50 p-6">{children}</main>
      </div>
    </div>
  );
}
